/**
 * Hand-rolled top-down layered layout for a gameplan Line.
 *
 * Slay-the-Spire-style: nodes flow top-to-bottom, each layer is a row,
 * forward edges drop straight down to the next layer, loop-back edges
 * (cycles) curve out to the right and arc back up.
 *
 * Algorithm:
 *   1. Detect back-edges with a DFS. An edge whose target is currently
 *      on the DFS stack is a cycle-closing edge; everything else is
 *      treated as forward.
 *   2. Layer assignment via longest-path on the forward DAG:
 *      layer(v) = 1 + max(layer(u)) over forward in-edges, or 0 if
 *      none. Stable enough for the small (≤ ~50 node) lines we expect.
 *   3. Column assignment within a layer: nodes are placed in the order
 *      they first appear in line.nodes — so the user's insertion order
 *      controls left-to-right placement.
 *   4. The result is a list of laid-out nodes (id + x + y) and edges
 *      annotated with their kind ('forward' | 'back'). The renderer
 *      uses those to pick path shapes.
 *
 * Pure / no React — easy to unit-test if we add eval later.
 */
import type { Line, LineEdge, LineNode } from '../types/gameplan';

export type LayoutNode = {
  node: LineNode;
  x: number;
  y: number;
  layer: number;
  col: number; // column index within its layer (for debugging)
};

export type LayoutEdge = {
  edge: LineEdge;
  kind: 'forward' | 'back';
  from: LayoutNode;
  to: LayoutNode;
};

export type LineLayout = {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  /** Width of the laid-out content in SVG units. */
  width: number;
  /** Height of the laid-out content in SVG units. */
  height: number;
};

// Layout constants. NODE_W/H are the drawn-card dimensions; COL_W/ROW_H
// are the distance between node centers. Margins keep the first row off
// the top edge so labels aren't clipped.
//
// Values were tightened in the mobile-polish pass: previous COL_W=170 /
// ROW_H=110 wasted horizontal & vertical space inside the narrow
// bottom sheet. Cards are unchanged so labels stay legible; we just
// pack them closer together. Smaller numbers also shorten every edge
// path which keeps the connectors visually compact.
export const NODE_W = 140;
export const NODE_H = 48;
export const COL_W = 152;
export const ROW_H = 86;
export const MARGIN_X = 20;
export const MARGIN_Y = 18;
// Extra width reserved on the right side so loop-back curves have room
// to bulge out without colliding with the panel's right edge.
export const LOOPBACK_GUTTER = 60;

export function layoutLine(line: Line): LineLayout {
  const nodes = line.nodes;
  const edges = line.edges;

  if (nodes.length === 0) {
    return { nodes: [], edges: [], width: 0, height: 0 };
  }

  // Adjacency: outgoing edges by node id. Self-loops are dropped from
  // the DAG (we'll still render them as back-edges).
  const outEdges = new Map<string, LineEdge[]>();
  for (const n of nodes) outEdges.set(n.id, []);
  for (const e of edges) {
    const list = outEdges.get(e.from);
    if (list) list.push(e);
  }

  // ---- 1. Back-edge detection via DFS ----
  // Iterative DFS keeps the stack out of JS recursion limits on
  // pathological lines (not expected, but cheap insurance).
  const state = new Map<string, 'unvisited' | 'inStack' | 'done'>();
  for (const n of nodes) state.set(n.id, 'unvisited');
  const backEdgeIds = new Set<string>();

  const dfs = (start: string) => {
    type Frame = { id: string; outIdx: number };
    const stack: Frame[] = [{ id: start, outIdx: 0 }];
    state.set(start, 'inStack');
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      const outs = outEdges.get(top.id) ?? [];
      if (top.outIdx >= outs.length) {
        state.set(top.id, 'done');
        stack.pop();
        continue;
      }
      const e = outs[top.outIdx++];
      const tgtState = state.get(e.to);
      if (tgtState === 'inStack' || e.to === e.from) {
        backEdgeIds.add(e.id);
      } else if (tgtState === 'unvisited') {
        state.set(e.to, 'inStack');
        stack.push({ id: e.to, outIdx: 0 });
      }
      // 'done' targets are cross/forward edges — kept as forward
      // edges, since they still flow downward in the layered layout
      // when target's layer > source's. The layering step below
      // tolerates them.
    }
  };

  // Start DFS from each true root (no in-edges). This produces the
  // most natural layering for trees and DAGs. Any node not reached
  // (e.g. inside a cycle whose root we missed) gets its own DFS so
  // every node ends up classified.
  const inCount = new Map<string, number>();
  for (const n of nodes) inCount.set(n.id, 0);
  for (const e of edges) inCount.set(e.to, (inCount.get(e.to) ?? 0) + 1);
  for (const n of nodes) {
    if (state.get(n.id) === 'unvisited' && (inCount.get(n.id) ?? 0) === 0) {
      dfs(n.id);
    }
  }
  for (const n of nodes) {
    if (state.get(n.id) === 'unvisited') dfs(n.id);
  }

  // ---- 2. Layer assignment via longest path on the forward DAG ----
  const layer = new Map<string, number>();
  for (const n of nodes) layer.set(n.id, 0);

  // Topological order of forward edges. Compute via Kahn's algorithm.
  const forwardInCount = new Map<string, number>();
  for (const n of nodes) forwardInCount.set(n.id, 0);
  for (const e of edges) {
    if (!backEdgeIds.has(e.id)) {
      forwardInCount.set(e.to, (forwardInCount.get(e.to) ?? 0) + 1);
    }
  }
  const ready: string[] = [];
  for (const n of nodes) {
    if ((forwardInCount.get(n.id) ?? 0) === 0) ready.push(n.id);
  }
  const topo: string[] = [];
  // Working copy of forwardInCount so we can decrement.
  const fc = new Map(forwardInCount);
  while (ready.length > 0) {
    const id = ready.shift()!;
    topo.push(id);
    for (const e of outEdges.get(id) ?? []) {
      if (backEdgeIds.has(e.id)) continue;
      const left = (fc.get(e.to) ?? 0) - 1;
      fc.set(e.to, left);
      if (left === 0) ready.push(e.to);
    }
  }
  // If topo didn't cover everything (shouldn't happen now that back
  // edges are removed, but defensive), append the rest at layer 0.
  for (const n of nodes) {
    if (!topo.includes(n.id)) topo.push(n.id);
  }
  for (const id of topo) {
    let l = 0;
    for (const e of edges) {
      if (e.to === id && !backEdgeIds.has(e.id)) {
        l = Math.max(l, (layer.get(e.from) ?? 0) + 1);
      }
    }
    layer.set(id, l);
  }

  // ---- 3. Column assignment within layer (barycenter heuristic) ----
  //
  // Goal: minimize gratuitous edge crossings. A naive insertion-order
  // pass puts the third child of a left-side parent on the right side
  // of the next layer (because it was added last), forcing its incoming
  // edge to cross unrelated edges.
  //
  // The barycenter heuristic places each node at the average column
  // position of its forward in-edges' sources. Process layers top-down
  // so by the time we compute layer L's columns, layer L-1's are known.
  // Ties broken by insertion order for stability.
  const nodeIndex = new Map<string, number>();
  nodes.forEach((n, i) => nodeIndex.set(n.id, i));

  // Forward parents per node, used for barycenter computation.
  const forwardParents = new Map<string, string[]>();
  for (const n of nodes) forwardParents.set(n.id, []);
  for (const e of edges) {
    if (backEdgeIds.has(e.id)) continue;
    forwardParents.get(e.to)?.push(e.from);
  }

  // Bin nodes by layer first so we can iterate top-down.
  const layerBins = new Map<number, string[]>();
  for (const n of nodes) {
    const l = layer.get(n.id) ?? 0;
    if (!layerBins.has(l)) layerBins.set(l, []);
    layerBins.get(l)!.push(n.id);
  }

  const colOf = new Map<string, number>();
  const byLayer = new Map<number, string[]>();
  const maxLayer = Math.max(...Array.from(layer.values()), 0);
  for (let L = 0; L <= maxLayer; L++) {
    const ids = layerBins.get(L) ?? [];
    if (ids.length === 0) continue;
    // Compute barycenter per id. Nodes with no forward parents fall
    // back to their insertion index — keeps roots and isolated nodes
    // in stable, predictable positions.
    const withBary = ids.map((id) => {
      const parents = forwardParents.get(id) ?? [];
      let bary: number;
      if (parents.length === 0) {
        // Use insertion index but scale it relative to the previous
        // layer so it doesn't collide weirdly with parent-based values.
        bary = nodeIndex.get(id) ?? 0;
      } else {
        const sum = parents.reduce(
          (s, p) => s + (colOf.get(p) ?? 0),
          0,
        );
        bary = sum / parents.length;
      }
      return { id, bary };
    });
    withBary.sort((a, b) => {
      if (a.bary !== b.bary) return a.bary - b.bary;
      return (nodeIndex.get(a.id) ?? 0) - (nodeIndex.get(b.id) ?? 0);
    });
    const sortedIds = withBary.map((w) => w.id);
    byLayer.set(L, sortedIds);
    sortedIds.forEach((id, i) => colOf.set(id, i));
  }

  const maxCols = Math.max(
    ...Array.from(byLayer.values()).map((arr) => arr.length),
  );
  const contentWidth = Math.max(NODE_W, maxCols * COL_W);

  const layouts = new Map<string, LayoutNode>();
  for (const [l, ids] of byLayer.entries()) {
    // Center this layer's nodes horizontally within contentWidth.
    const rowWidth = ids.length * COL_W;
    const startX = MARGIN_X + (contentWidth - rowWidth) / 2 + COL_W / 2;
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const node = nodes.find((n) => n.id === id)!;
      layouts.set(id, {
        node,
        x: startX + i * COL_W,
        y: MARGIN_Y + l * ROW_H + NODE_H / 2,
        layer: l,
        col: i,
      });
    }
  }

  // ---- 4. Annotate edges ----
  const outEdgesLaid: LayoutEdge[] = [];
  for (const e of edges) {
    const from = layouts.get(e.from);
    const to = layouts.get(e.to);
    if (!from || !to) continue; // dangling edge — shouldn't happen
    outEdgesLaid.push({
      edge: e,
      kind: backEdgeIds.has(e.id) ? 'back' : 'forward',
      from,
      to,
    });
  }

  // ---- 5. Bounding box ----
  // maxLayer was already computed above; reuse it instead of
  // recomputing (and to satisfy block-scoped-redeclaration).
  //
  // The LOOPBACK_GUTTER is reserved space on the RIGHT for the arcs
  // of back-edges (loops) to bow out into. Only add it when the line
  // actually HAS a back edge — otherwise it's dead space that shifts
  // the (centered-within-contentWidth) node column left of the lane
  // center, making straight lines look left-aligned. With the gutter
  // gone for loop-free lines, the node column centers in its lane.
  const hasBackEdges = backEdgeIds.size > 0;
  const totalWidth =
    MARGIN_X * 2 + contentWidth + (hasBackEdges ? LOOPBACK_GUTTER : 0);
  const totalHeight = MARGIN_Y * 2 + maxLayer * ROW_H + NODE_H;

  return {
    nodes: Array.from(layouts.values()),
    edges: outEdgesLaid,
    width: totalWidth,
    height: totalHeight,
  };
}
